import PublicLayout from "@/components/PublicLayout";
import { Lock, ShieldCheck, Cross, Database, Mail } from "lucide-react";

export default function Privacy() {
  return (
    <PublicLayout>
      <section className="celestial-bg">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20">
          <p className="text-xs uppercase tracking-widest text-brand-quiet">Data Privacy</p>
          <h1 className="mt-3 font-serif text-5xl sm:text-6xl tracking-tight text-brand-ink">
            Your heart is safe here.
          </h1>
          <p className="mt-6 text-lg text-brand-quiet leading-relaxed max-w-2xl">
            Path to Repentance was built around one principle: what you confess, journal, or pray
            stays between you and God. The rest of this page is the careful, plain-language
            version of how we honor that promise.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-14 space-y-10">
        <Block icon={Lock} title="All your data is private — period.">
          Every sin you record, every prayer you write, and every conversation you have with the
          AI companion is stored as <em>your private record</em>. We do not sell it, share it, or
          analyze it for advertising. No human at Path to Repentance reads your entries.
        </Block>

        <Block icon={Database} title="What we store, and why.">
          <ul className="list-disc list-outside ml-5 space-y-2">
            <li><strong>Account</strong>: your email, a chosen name (or anonymous label), and a hashed password (bcrypt — we never see your plaintext password).</li>
            <li><strong>Sin tracker entries</strong>: category, severity, trigger, emotional state, notes, resisted flag, confessed-to-priest flag, recovery actions. Tied to your account ID only.</li>
            <li><strong>Prayer journal</strong>: title, body, answered status, date.</li>
            <li><strong>AI Companion conversations</strong>: your messages and the AI's replies — so you can revisit and continue them.</li>
            <li><strong>Donation transactions</strong>: Stripe session id, amount, and status. We never store your card number — Stripe handles that.</li>
          </ul>
        </Block>

        <Block icon={ShieldCheck} title="How we protect it.">
          <ul className="list-disc list-outside ml-5 space-y-2">
            <li>Passwords are hashed with bcrypt; even our admins cannot see them.</li>
            <li>Authentication uses signed JWT tokens delivered over <strong>httpOnly cookies</strong> (your browser never exposes them to JavaScript).</li>
            <li>All traffic to and from the app is over HTTPS / TLS.</li>
            <li>Each record is scoped to your user ID — no other user can read your entries, and database queries are filtered server-side on every request.</li>
            <li>Anonymous mode hides your chosen name across the app while keeping your data accessible to you.</li>
          </ul>
        </Block>

        <Block icon={Cross} title="Your rights.">
          <ul className="list-disc list-outside ml-5 space-y-2">
            <li><strong>Access</strong>: everything you've recorded is visible to you inside the app.</li>
            <li><strong>Deletion</strong>: you may delete any sin entry, prayer, or chat conversation at any time. Deleting your account erases every record we hold about you, irreversibly.</li>
            <li><strong>Export</strong>: an export feature is on our roadmap. Until then, contact us and we will send you a copy of your data.</li>
            <li><strong>Withdrawal</strong>: you may withdraw consent and close your account at any time, for any reason.</li>
          </ul>
        </Block>

        <Block icon={Mail} title="Third parties.">
          <ul className="list-disc list-outside ml-5 space-y-2">
            <li><strong>Anthropic Claude</strong> processes your AI Companion messages to produce its biblical responses. We send only your message and the system prompt — never your account email, sin entries, or prayer journal. Anthropic does not retain your messages for training when accessed via the API.</li>
            <li><strong>Stripe</strong> processes donations. We pass them an opaque session ID and your donation amount; they handle card data on their PCI-DSS infrastructure.</li>
            <li><strong>MongoDB</strong> stores your records on encrypted disks.</li>
            <li>We use no advertising trackers, no behavioral analytics, no third-party fingerprinting.</li>
          </ul>
        </Block>

        <Block icon={Lock} title="GDPR / general data principles.">
          Path to Repentance adopts a minimal-data design: we collect only what makes the app function.
          You are the data subject and controller of your own spiritual record. Requests for access,
          rectification, restriction, portability, erasure, or to lodge a complaint can be sent to
          the email below and will be honored within 30 days.
        </Block>

        <div className="rounded-2xl bg-white border border-brand-line p-7">
          <p className="text-sm text-brand-quiet">
            Questions or data requests? Write to <span className="text-brand-ink font-medium">noreply@pathtorepentance.com</span>.
          </p>
          <p className="text-xs text-brand-muted mt-3">
            This page was last updated on {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}

function Block({ icon: Icon, title, children }) {
  return (
    <article className="flex gap-5">
      <span className="shrink-0 w-12 h-12 rounded-full bg-brand-mist text-brand-skyDark grid place-items-center">
        <Icon className="w-5 h-5" strokeWidth={1.6} />
      </span>
      <div className="flex-1">
        <h2 className="font-serif text-3xl text-brand-ink tracking-tight">{title}</h2>
        <div className="mt-3 text-brand-quiet leading-relaxed">{children}</div>
      </div>
    </article>
  );
}
